/**
 * Preset Manager Modal
 *
 * Interface for viewing and managing global layer presets.
 * Part of Layer Drawer 2.0 - Phase 5 (Presets & Advanced Features).
 *
 * Features:
 * - View all global presets
 * - Delete global presets
 * - Instructions for creating new presets via Layer Drawer
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faGlobe, faInfoCircle, faSync } from '@fortawesome/free-solid-svg-icons';
import { layerPresetService } from '../../../../services/layerPresetService';
import { LayerPreset } from '../../../../types/layerPreset';
import './PresetManagerModal.scss';
import { useConfirm } from '../../../common/AppConfirm';
import AppModal from '../../../common/AppModal';
import { useMessages } from '../../../common/AppMessages';

interface PresetManagerModalProps {
  onClose: () => void;
}

export const PresetManagerModal: React.FC<PresetManagerModalProps> = ({ onClose }) => {
  const askConfirm = useConfirm();
  const { showMessage } = useMessages();
  const [presets, setPresets] = useState<LayerPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Fetch global presets
  const fetchPresets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const globalPresets = await layerPresetService.getGlobalPresets();
      setPresets(globalPresets);
    } catch (err) {
      console.error('Failed to fetch global presets:', err);
      setError('Failed to load global presets');
    } finally {
      setLoading(false);
    }
  }, [askConfirm, showMessage]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  // Delete a preset
  const handleDeletePreset = useCallback(async (presetId: string, presetName: string) => {
    const confirmed = await askConfirm({
      title: 'Delete global preset',
      message: `Are you sure you want to delete the global preset "${presetName}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!confirmed) return;

    setDeleting(presetId);
    try {
      await layerPresetService.deletePreset(presetId);
      setPresets(prev => prev.filter(p => p.id !== presetId));
    } catch (err) {
      console.error('Failed to delete preset:', err);
      showMessage('Failed to delete preset. Please try again.', 'error');
    } finally {
      setDeleting(null);
    }
  }, []);

  return (
    <AppModal
      isOpen
      onClose={onClose}
      width={700}
      maxHeight="90vh"
      header={
        <h2 className="modal-title">
          <FontAwesomeIcon icon={faGlobe} /> Global Preset Manager
        </h2>
      }
    >
      <div className="preset-manager-modal">
        <div className="preset-manager-content">
          {/* Instructions */}
          <div className="preset-manager-info">
            <FontAwesomeIcon icon={faInfoCircle} />
            <div>
              <strong>How to create a global preset:</strong>
              <ol>
                <li>Open the <strong>Layer Drawer</strong> on the map</li>
                <li>Configure layers as desired (enable, set opacity, reorder)</li>
                <li>Click the <strong>Save</strong> button in the Active tab</li>
                <li>Check <strong>"Make this a global preset"</strong></li>
                <li>Enter a name and save</li>
              </ol>
            </div>
          </div>

          {/* Preset List */}
          <div className="preset-list-section">
            <div className="preset-list-header">
              <h3>Global Presets</h3>
              <button
                className="btn btn-icon"
                onClick={fetchPresets}
                disabled={loading}
                title="Refresh"
              >
                <FontAwesomeIcon icon={faSync} spin={loading} />
              </button>
            </div>

            {loading && (
              <div className="preset-list-loading">
                <FontAwesomeIcon icon={faSync} spin /> Loading presets...
              </div>
            )}

            {error && (
              <div className="preset-list-error">
                {error}
              </div>
            )}

            {!loading && !error && presets.length === 0 && (
              <div className="preset-list-empty">
                <p>No global presets configured yet.</p>
                <p className="hint">Create one using the Layer Drawer on the map.</p>
              </div>
            )}

            {!loading && !error && presets.length > 0 && (
              <div className="preset-list">
                {presets.map(preset => (
                  <div key={preset.id} className="preset-item">
                    <div className="preset-item-info">
                      <div className="preset-item-name">{preset.name}</div>
                      {preset.description && (
                        <div className="preset-item-description">{preset.description}</div>
                      )}
                      <div className="preset-item-meta">
                        {preset.preset_data.layers.length} layers
                        {preset.preset_data.liveLayers.length > 0 && (
                          <>, {preset.preset_data.liveLayers.length} live layers</>
                        )}
                        {' • '}Created {new Date(preset.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="preset-item-actions">
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeletePreset(preset.id, preset.name)}
                        disabled={deleting === preset.id}
                        title="Delete preset"
                      >
                        <FontAwesomeIcon icon={faTrash} spin={deleting === preset.id} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </AppModal>
  );
};
